import { isValidUrl } from '@/utils/url'
import { findAllLinksInBeefreeTemplate } from '@/utils/message'

export default {
  data() {
    return {
      invalidUrls: [],
      invalidEmailAddresses: [],
      // A/B testing validation results
      invalidUrlsVariantA: [],
      invalidEmailAddressesVariantA: [],
      invalidUrlsVariantB: [],
      invalidEmailAddressesVariantB: []
    }
  },

  computed: {
    warningMessage() {
      
      const invalidUrlsTextList = this.invalidUrls.map(rawUrl => {
        let safeUrl = rawUrl
        try {
          safeUrl = decodeURIComponent(rawUrl)
        } catch (e) {
          // Leave the raw URL if decoding fails (malformed percent-encoding)
        }
        return `<li>${safeUrl}</li>`
      }).join('')
      
      const invalidEmailAddressesTextList = this.invalidEmailAddresses.map(rawEmail => {
        let safeEmail = rawEmail
        try {
          safeEmail = decodeURIComponent(rawEmail)
        } catch (e) {
          // Leave the raw email if decoding fails
        }
        return `<li>${safeEmail}</li>`
      }).join('')

      // A/B variant-specific lists
      const invalidUrlsVariantAList = this.invalidUrlsVariantA.map(rawUrl => {
        let safeUrl = rawUrl
        try {
          safeUrl = decodeURIComponent(rawUrl)
        } catch (e) {
          // Leave the raw URL if decoding fails
        }
        return `<li>${safeUrl}</li>`
      }).join('')

      const invalidUrlsVariantBList = this.invalidUrlsVariantB.map(rawUrl => {
        let safeUrl = rawUrl
        try {
          safeUrl = decodeURIComponent(rawUrl)
        } catch (e) {
          // Leave the raw URL if decoding fails
        }
        return `<li>${safeUrl}</li>`
      }).join('')

      // A/B variant email addresses
      const invalidEmailAddressesVariantAList = this.invalidEmailAddressesVariantA.map(rawEmail => {
        let safeEmail = rawEmail
        try {
          safeEmail = decodeURIComponent(rawEmail)
        } catch (e) {
          // Leave the raw email if decoding fails
        }
        return `<li>${safeEmail}</li>`
      }).join('')

      const invalidEmailAddressesVariantBList = this.invalidEmailAddressesVariantB.map(rawEmail => {
        let safeEmail = rawEmail
        try {
          safeEmail = decodeURIComponent(rawEmail)
        } catch (e) {
          // Leave the raw email if decoding fails
        }
        return `<li>${safeEmail}</li>`
      }).join('')
      
      // Build message parts
      let messageParts = []
      
      // Always add the header if we have any invalid content
      if (this.hasAnyInvalidContent) {
        messageParts.push(`<p><b>There are some invalid URLs in your email.</b></p>`)
        
        // Add instruction text if we have any invalid URLs (not just email addresses)
        if (this.invalidUrls.length > 0 || this.invalidUrlsVariantA.length > 0 || this.invalidUrlsVariantB.length > 0) {
          messageParts.push(`<p>Use https:// for secure links, and http:// for non-secure links.</p>`)
        }
      }
      
      if (this.invalidUrls.length > 0) {
        messageParts.push(`<p>The following URLs are affected: <ul>${invalidUrlsTextList}</ul></p>`)
      }
      
      if (this.invalidUrlsVariantA.length > 0) {
        messageParts.push(`<p>Invalid URLs in Design A:</p><ul>${invalidUrlsVariantAList}</ul>`)
      }
      
      if (this.invalidUrlsVariantB.length > 0) {
        messageParts.push(`<p>Invalid URLs in Design B:</p><ul>${invalidUrlsVariantBList}</ul>`)
      }
      
      if (this.invalidEmailAddresses.length > 0) {
        messageParts.push(`<p>Be sure to include a single @ in these email addresses: <ul>${invalidEmailAddressesTextList}</ul></p>`)
      }
      
      if (this.invalidEmailAddressesVariantA.length > 0) {
        messageParts.push(`<p>Invalid email addresses in Design A:</p><ul>${invalidEmailAddressesVariantAList}</ul>`)
      }
      
      if (this.invalidEmailAddressesVariantB.length > 0) {
        messageParts.push(`<p>Invalid email addresses in Design B:</p><ul>${invalidEmailAddressesVariantBList}</ul>`)
      }
      
      if (this.hasAnyInvalidContent) {
        messageParts.push(`<br />
        <p>We'll flag any link issues we spot, but the safest step is to give your links a quick test yourself before sending.</p>`)

        messageParts.push(`<p><a href="#">Return to the editor</a></p>`)
      }
      
      return messageParts.join('')
    },
    
    hasInvalidUrls() {
      return this.invalidUrls.length > 0
    },
    
    hasInvalidEmailAddresses() {
      return this.invalidEmailAddresses.length > 0
    },
    
    hasAnyInvalidContent() {
      return this.invalidUrls.length > 0 || 
             this.invalidEmailAddresses.length > 0 ||
             this.invalidUrlsVariantA.length > 0 ||
             this.invalidEmailAddressesVariantA.length > 0 ||
             this.invalidUrlsVariantB.length > 0 ||
             this.invalidEmailAddressesVariantB.length > 0
    }
  },

  methods: {
    // Helper function to get all anchor links from HTML (for non-beefree templates)
    getAllAnchorLinks(html) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      return Array.from(doc.querySelectorAll('a'))
        .map(link => link.getAttribute('href'))
        .filter(href =>
          href &&                             // not null/undefined
          href !== '#' &&                     // not just '#'
          !/\{\{.*\}\}/.test(href)            // not a template like {{something}}
        )
    },

    validateEmailUrls(emailMessage, emailVariants) {
      // Reset all validation arrays
      this.resetValidation()
      
      // Check if we have A/B variants to validate
      // Only use variants if explicitly passed and both A and B exist
      if (emailVariants !== null && emailVariants && emailVariants.A && emailVariants.B) {
        // Always validate both A/B variants to show combined errors
        this.validateVariant(emailVariants.A, 'A')
        this.validateVariant(emailVariants.B, 'B')
      } else {
        // Regular validation for single email
        this.validateSingleEmail(emailMessage)
      }
    },

    validateSingleEmail(emailMessage) {
      const html = emailMessage?.meta?.presentation?.template
      
      if (!html) return
      
      let urls = []
      
      if (emailMessage?.meta?.presentation?.templateType !== 'beefree') {
        // if it's not a beefree template, scan for URLs in anchor tags
        urls = this.getAllAnchorLinks(html)
      } else {  
        // if it is beefree, use the beefree-specific function
        urls = findAllLinksInBeefreeTemplate(html)
      }

      // Split the URLs with and without @ symbols into two lists
      this.invalidUrls = urls.filter(url => !isValidUrl(url) && !url.includes('@')) // regular URLs
      this.invalidEmailAddresses = urls.filter(url => url.includes('@') && !isValidUrl(url)) // email addresses
    },

    validateVariant(variantMessage, variantKey) {
      const html = variantMessage?.meta?.presentation?.template
      
      if (!html) return
      
      let urls = []
      
      if (variantMessage?.meta?.presentation?.templateType !== 'beefree') {
        // if it's not a beefree template, scan for URLs in anchor tags
        urls = this.getAllAnchorLinks(html)
      } else {  
        // if it is beefree, use the beefree-specific function
        urls = findAllLinksInBeefreeTemplate(html)
      }

      // Store results in variant-specific arrays
      if (variantKey === 'A') {
        this.invalidUrlsVariantA = urls.filter(url => !isValidUrl(url) && !url.includes('@'))
        this.invalidEmailAddressesVariantA = urls.filter(url => url.includes('@') && !isValidUrl(url))
      } else if (variantKey === 'B') {
        this.invalidUrlsVariantB = urls.filter(url => !isValidUrl(url) && !url.includes('@'))
        this.invalidEmailAddressesVariantB = urls.filter(url => url.includes('@') && !isValidUrl(url))
      }
    },

    resetValidation() {
      this.invalidUrls = []
      this.invalidEmailAddresses = []
      this.invalidUrlsVariantA = []
      this.invalidEmailAddressesVariantA = []
      this.invalidUrlsVariantB = []
      this.invalidEmailAddressesVariantB = []
    }
  }
}